import React from 'react';
import { AllState } from 'constants/common';
import { fileNameFromKey } from 'utils/file';
import type {
    AdditionalW9Item,
    GovEnrollmentByState,
    IrsLetterDto,
    MedicareManagementDto,
    ServiceAddressItem,
} from './types';
import s from './common.module.less';

// 四个板块的只读渲染（截图样式），GroupReviewView 和 Settings 的 Payer requirements tab 共用。
// 板块本身"有数据才显示"的判断由调用方负责，这里只管展示。

const getStateName = (code: string) => AllState.find((i) => i.dictKey === code)?.content || code;

// 空值占位：调用方会把「按 panel 触发应有、但还没存过数据」的州合成占位行传进来（旧数据补录场景）
const NotProvided = () => <span className={s.viewEmpty}>Not provided</span>;

export const ServiceAddressView = ({ items }: { items: ServiceAddressItem[] }) => (
    <div className={s.viewGrid}>
        {items.map((a) => {
            const joined = [a.addressLine1, a.addressLine2, a.city, a.zip].filter(Boolean).join(', ');
            return (
                <div className={s.viewItem} key={a.state}>
                    <div className={s.viewLabel}>{getStateName(a.state)}</div>
                    <div className={s.viewValue}>{joined || <NotProvided />}</div>
                </div>
            );
        })}
    </div>
);

const govLine = (g: GovEnrollmentByState) => {
    if (g.enrolled === undefined || g.enrolled === null) return <NotProvided />;
    if (!g.enrolled) return 'Not enrolled';
    return g.certificationNumber ? `Enrolled · ${g.certificationNumber}` : 'Enrolled';
};

const managerLine = (mgmt?: MedicareManagementDto | null) => {
    if (mgmt?.hasManager === undefined || mgmt?.hasManager === null) return <NotProvided />;
    if (!mgmt.hasManager) return 'No';
    const names = (mgmt.managers || []).map((m) => [m.firstName, m.lastName].filter(Boolean).join(' ')).filter(Boolean);
    return names.length ? names.join(', ') : <NotProvided />;
};

// management 不传 = 不展示这一行（Medicare 全部已注册时不问这一题）
export const GovProgramView = ({
    medicare,
    medicaid,
    management,
}: {
    medicare: GovEnrollmentByState[];
    medicaid: GovEnrollmentByState[];
    management?: MedicareManagementDto | null | false;
}) => (
    <div className={s.viewGrid}>
        {medicare.map((g) => (
            <div className={s.viewItem} key={`medicare-${g.state}`}>
                <div className={s.viewLabel}>Medicare · {getStateName(g.state)}</div>
                <div className={s.viewValue}>{govLine(g)}</div>
            </div>
        ))}
        {medicaid.map((g) => (
            <div className={s.viewItem} key={`medicaid-${g.state}`}>
                <div className={s.viewLabel}>Medicaid · {getStateName(g.state)}</div>
                <div className={s.viewValue}>{govLine(g)}</div>
            </div>
        ))}
        {management !== false && management !== undefined && (
            <div className={s.viewItem}>
                <div className={s.viewLabel}>Anyone else managing the practice</div>
                <div className={s.viewValue}>{managerLine(management)}</div>
            </div>
        )}
    </div>
);

export const IrsLetterView = ({ value }: { value: IrsLetterDto }) => (
    <div className={s.viewGrid}>
        <div className={s.viewItem}>
            <div className={s.viewLabel}>Legal name on the letter</div>
            <div className={s.viewValue}>{(value.legalName || '').trim() || <NotProvided />}</div>
        </div>
        <div className={s.viewItem}>
            <div className={s.viewLabel}>IRS letter (CP 575 / 147C)</div>
            <div className={s.viewValue}>
                {value.fileUrl ? value.fileName || fileNameFromKey(value.fileUrl) : <NotProvided />}
            </div>
        </div>
    </div>
);

export const AetnaFocusView = ({ focusAreas }: { focusAreas: string[] }) =>
    focusAreas.length ? (
        <ul className={s.viewList}>
            {focusAreas.map((area) => (
                <li key={area}>{area}</li>
            ))}
        </ul>
    ) : (
        <div className={s.viewEmpty}>Not provided</div>
    );

// 每条 W-9 一行（primary 由调用方排最前）：label = 表上名字（或 taxId 兜底）+ Legal/DBA 标注
export const AdditionalW9View = ({ items }: { items: AdditionalW9Item[] }) => (
    <div className={s.viewGrid}>
        {items.map((w, i) => {
            const name = (w.nameOnW9 || '').trim() || w.taxId || `W-9 form ${i + 1}`;
            const kind = w.nameType === 'DBA' ? 'DBA' : 'Legal name';
            return (
                <div className={s.viewItem} key={`${w.taxId || ''}-${i}`}>
                    <div className={s.viewLabel}>
                        W-9 · {name} ({kind})
                    </div>
                    <div className={s.viewValue}>
                        {w.taxId ? `${w.taxId} · ` : ''}
                        {w.fileUrl ? 'Uploaded' : <NotProvided />}
                    </div>
                </div>
            );
        })}
    </div>
);
